import { useCallback, useReducer, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getSearchedAssets,
  postRequestSignedUrls
} from '../../../assets/requests'
import placeholderImg from '../../../assets/images/camera.jpg'
import { reducer } from '../reducer'

const initialSearchState = {
  navigation: [],
  assets: [],
  showAssets: false,
  rawAssets: {},
  signedAssets: {},
  publishedAssets: {},
  modalActive: false,
  modalData: {},
  modalStatus: 'Image',
  selectedCategories: [],
  loading: {
    canLoadMore: true,
    currentPage: 1,
    isLoading: false,
    pageSize: 50
  }
}

/**
 * Hook for managing search functionality
 * Handles search form state, API calls, and search results
 */
export const useSearch = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [state, dispatch] = useReducer(reducer, initialSearchState)

  /**
   * Signs assets with secure URLs for thumbnails and full images
   */
  const signAssets = useCallback(async (rawAssets, userId) => {
    const thumbnailKeysToSign = []
    const thumbnailKeyIdx = []

    rawAssets.forEach((a, idx) => {
      const key = a.thumbnails?.[1] || null
      if (key) {
        thumbnailKeysToSign.push(key)
        thumbnailKeyIdx.push(idx)
      }
    })

    const s3Uris = []
    const s3UriIdx = []

    // Only collect s3Uris that need signing
    rawAssets.forEach((asset, idx) => {
      const s3uri = asset.s3uri
      if (
        s3uri &&
        !s3uri.startsWith('https://') &&
        !s3uri.includes('X-Amz-Algorithm')
      ) {
        s3Uris.push(s3uri)
        s3UriIdx.push(idx)
      }
    })

    const [signedThumbs, signedS3] = await Promise.all([
      thumbnailKeysToSign.length > 0
        ? postRequestSignedUrls({ userId, payload: thumbnailKeysToSign })
        : [],
      s3Uris.length > 0
        ? postRequestSignedUrls({ userId, payload: s3Uris })
        : []
    ])

    const signedObj = {}
    rawAssets.forEach((asset, i) => {
      // Map signed thumbnail back to this asset if present
      const thumbIndex = thumbnailKeyIdx.indexOf(i)
      const signedThumb = thumbIndex !== -1 ? signedThumbs[thumbIndex] : null

      // Use the mapping index for s3uri updates
      const s3UriSignedIndex = s3UriIdx.indexOf(i)
      const signedS3Uri =
        s3UriSignedIndex !== -1 && signedS3[s3UriSignedIndex]
          ? signedS3[s3UriSignedIndex]
          : asset.s3uri // Keep original if already signed or not found

      signedObj[asset.id] = {
        ...asset,
        src: signedThumb || asset.thumbnails?.[1] || placeholderImg,
        s3uri: signedS3Uri
      }
    })
    return signedObj
  }, [])

  /**
   * Performs search API call and updates state
   */
  const performSearch = useCallback(
    async (searchQuery, userId) => {
      if (!searchQuery || !userId) return

      try {
        setIsLoading(true)
        const rawAssets = await getSearchedAssets(userId, searchQuery)
        const signedAssets = await signAssets(rawAssets, userId)

        dispatch({ type: 'set_assets', payload: signedAssets })
        dispatch({ type: 'set_published_assets', payload: signedAssets })
      } catch (err) {
        console.error('Search fetch error:', err)
      } finally {
        setIsLoading(false)
      }
    },
    [signAssets]
  )

  /**
   * Handles search form submission
   */
  const handleSearchSubmit = useCallback(
    e => {
      e.preventDefault()
      if (!searchTerm.trim()) {
        console.log('No search term entered.')
        return
      }
      navigate(`/search?term=${encodeURIComponent(searchTerm)}`)
      setSearchTerm('')
    },
    [searchTerm, navigate]
  )

  /**
   * Handles search input changes
   */
  const handleSearchInput = useCallback(e => {
    const term = e.target.value
    setSearchTerm(term)
  }, [])

  /**
   * Handles asset click in search results
   */
  const handleAssetClick = useCallback(
    assetId => {
      dispatch({
        type: 'set_modal_data',
        payload: {
          assetId,
          asset: state.publishedAssets[assetId]
        }
      })
      dispatch({ type: 'set_modal_active', payload: true })
    },
    [state.publishedAssets]
  )

  /**
   * Closes modal
   */
  const closeModal = useCallback(() => {
    dispatch({ type: 'set_modal_active', payload: false })
  }, [])

  /**
   * Loads more search results
   */
  const loadMoreResults = useCallback(() => {
    dispatch({
      type: 'set_loading',
      payload: {
        currentPage: state.loading.currentPage + 1
      }
    })
  }, [state.loading.currentPage])

  /**
   * Resets search state
   */
  const resetSearch = useCallback(() => {
    dispatch({ type: 'reset_state' })
    setSearchTerm('')
    setIsLoading(false)
  }, [])

  return {
    // State
    searchTerm,
    isLoading,
    state,
    dispatch,

    // Actions
    handleSearchSubmit,
    handleSearchInput,
    handleAssetClick,
    closeModal,
    loadMoreResults,
    performSearch,
    resetSearch,

    // Utilities
    setSearchTerm
  }
}
